import { clone, exists } from '@jkhy/frontend-utils/functions';
import { UserSummaryModelDto as BoCompanyUserSummaryModelDto } from '@treasury/api/bo';
import { UserSummaryModelDto as ConsumerUserSummaryModelDto } from '@treasury/api/channel';
import { DomainEntity } from '../../../shared';

export const nullConsumerUserSummary: ConsumerUserSummaryModelDto = {
    id: 0,
    userId: '',
    status: '',
    isLocked: false,
    isAdmin: false,
    isSuperUser: false,
    isActive: false,
    isEditor: false,
    isMfaLocked: false,
};

export const nullBoCompanyUserSummary: BoCompanyUserSummaryModelDto = {
    ...nullConsumerUserSummary,
    authenticationMethodId: 0,
};

/**
 * BUSINESS LOGIC
 * Pending and FI Pending can potentially be moved as a enrollment sub-status
 * New - can go away be cause it always coensides with Not Enrolled
 * Edited - can be moved into the profile column as a sub-status
 */

export enum PublishState {
    None = '',
    Pending = 'Pending',
    FiPending = 'FiPending',
    New = 'New',
    Edited = 'Edited',
}

export enum Status {
    Enrolled = 'Enrolled',
    NotEnrolled = 'Not Enrolled',
}
export class CompanyUserSummary extends DomainEntity<
    ConsumerUserSummaryModelDto | BoCompanyUserSummaryModelDto
> {
    constructor(dto = clone(nullConsumerUserSummary)) {
        super(dto);
    }

    public lastViewed?: string;

    public createDefault(nullUserSummary = nullConsumerUserSummary): this {
        return new CompanyUserSummary(clone(nullUserSummary)) as this;
    }

    public get id() {
        return this.dto.id;
    }

    public get userId() {
        return this.dto.userId;
    }

    public get digitalId() {
        return this.dto.digitalId;
    }

    public get userName() {
        return this.dto.userName;
    }

    public get userNameAndId() {
        const roles = this.roles.join(', ');
        return roles
            ? `${this.userName} (${this.userId}) - ${roles}`
            : `${this.userName} (${this.userId})`;
    }

    public get enrolledStatus() {
        return this.dto.status;
    }

    public get isActive() {
        return !!this.dto.isActive;
    }

    public set isActive(value: boolean) {
        this.dto.isActive = value;
    }

    public get isEnrolled() {
        return this.dto.status === Status.Enrolled;
    }

    public get isNotEnrolled() {
        return this.dto.status === Status.NotEnrolled;
    }

    public get canApproveReject() {
        return this.dto.publishState === PublishState.Pending && !this.dto.isEditor;
    }

    public get isPending() {
        return this.dto.publishState === PublishState.Pending;
    }

    public get isFiPending() {
        return this.dto.publishState === PublishState.FiPending;
    }

    public get canEdit() {
        return (
            this.dto.publishState !== PublishState.Pending &&
            this.dto.publishState !== PublishState.FiPending
        );
    }

    public get canCopy() {
        return this.dto.isActive && this.dto.status === Status.Enrolled;
    }

    public get hasDigitalId() {
        return !!this.digitalId?.trim();
    }

    public get publishState() {
        return this.dto.publishState;
    }

    /**
     * Determines if the UIS enrollment link can be resent to the user.
     *
     * @returns {boolean} - True if the user is active, enrolled, and does not have a digital ID; otherwise, false.
     */
    public get canResendUisEnrollmentLink() {
        return this.isActive && this.isEnrolled && !this.hasDigitalId;
    }

    public get isSuperUser() {
        return this.dto.isSuperUser;
    }

    public get isAdmin() {
        return this.dto.isAdmin;
    }

    public get isEditor() {
        return this.dto.isEditor;
    }

    public get isUser() {
        return !this.isAdmin && !this.isSuperUser;
    }

    public get isLocked() {
        return this.dto.isLocked;
    }

    public get isNew() {
        return this.dto.publishState === PublishState.New;
    }

    public get securityAndPasswordSettingsLink() {
        return this.dto.securityAndPasswordSettingsLink as string | URL | undefined;
    }

    /**
     * Determines if the supplied DTO is using the BO or the Consumer shape
     */
    public isBoDto(): this is this & { dto: BoCompanyUserSummaryModelDto } {
        const boDto = this.dto as BoCompanyUserSummaryModelDto;
        return exists(boDto) && 'authenticationMethodId' in boDto;
    }

    /**
     * Following properties are only available on the BO shape
     */
    public get authenticationMethodId(): number | undefined {
        return this.isBoDto() ? this.dto.authenticationMethodId : undefined;
    }

    public get isAuthenticationLocked(): boolean | undefined {
        return this.isBoDto() ? this.dto.isAuthenticationLocked : undefined;
    }

    public get isAuthenticationSuspended(): string | undefined {
        return this.isBoDto() ? this.dto.isAuthenticationSuspended : undefined;
    }

    public get utcSuspendEndDate(): string | undefined {
        return this.isBoDto() ? this.dto.utcSuspendEndDate : undefined;
    }

    public get roles() {
        const roles: string[] = [];
        if (this.isAdmin) {
            roles.push('Admin');
        }
        if (this.isSuperUser) {
            roles.push('Super User');
        }
        if (this.isUser) {
            roles.push('User');
        }
        return roles;
    }
}
